import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import Avatar from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import {formatPersianDate, goalUpdateApi} from "../../../models/goal-update/goal-update-api";
import GoalUpdateViewDialog, {StatusChip} from "./goal-update-view-dialog";
import "./goal-updates.css";
import {richTextToPlain} from "../../../system/components/rich-text";

// The goal page's left column: every update of the goal, newest first (docs/tasks/17-goal-updates.md). A click opens the
// view modal. Entries from the old `goal.timeline` field are listed too.

const KIND_LABELS = {period_moved: "انتقال به بازه بعدی", period_closed: "بسته شدن در پایان بازه"};

function legacyEntries(goalData) {
    const timeline = Array.isArray(goalData?.timeline) ? goalData.timeline : [];
    return timeline.map((entry, index) => ({
        documentId: null,
        key: `legacy-${index}`,
        kind: "update",
        goalStatus: entry?.goalStatus,
        briff: entry?.briff || "",
        achievements: Array.isArray(entry?.achievements) ? entry.achievements : [],
        challenges: Array.isArray(entry?.challenges) ? entry.challenges : [],
        todos: Array.isArray(entry?.todos) ? entry.todos : [],
        createdAt: entry?.createdAt,
        author: null,
        recipients: [],
        goal: goalData ? {id: goalData.id, documentId: goalData.documentId, title: goalData.title} : null,
    }));
}

function GoalUpdatesList({goalData}) {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (!goalData?.documentId) return;
        let cancelled = false;
        goalUpdateApi.list(goalData.documentId)
            .then((result) => { if (!cancelled) setData(result); })
            .catch(() => { if (!cancelled) setData({updates: [], failed: true}); });
        return () => { cancelled = true; };
    }, [goalData?.documentId, goalData?.lastUpdatedAt, goalData?.goalStatus]);

    const updates = [...(data?.updates || []), ...legacyEntries(goalData)]
        .sort((left, right) => new Date(right.createdAt || 0) - new Date(left.createdAt || 0));

    return (
        <div className="gu flex flex-column gap-3" dir="rtl">
            <div className="flex align-items-center justify-content-between">
                <span className="gu-section-title">بروزرسانی‌های هدف</span>
                {data?.canUpdate && (
                    <button type="button" className="gu-soft-button gu-soft-button--primary" style={{height: "2rem", fontSize: ".72rem"}} onClick={() => navigate(`/goals/${goalData.documentId}/update-status`)}>
                        <Icon icon="mi mi:add" className="text-base"/>بروزرسانی جدید
                    </button>
                )}
            </div>
            {!data && <span className="text-xs gu-muted">در حال دریافت…</span>}
            {data && !updates.length && (
                <div className="gu-card p-3 text-xs gu-muted" style={{lineHeight: 1.9}}>
                    هنوز بروزرسانی‌ای ثبت نشده است. با «بروزرسانی هدف» وضعیت هدف را برای همه افراد زیر آن اعلام کنید.
                </div>
            )}
            {updates.map((update) => (
                <button type="button" key={update.documentId || update.key} className="gu-history-item" onClick={() => setSelected(update)}>
                    <div className="flex align-items-center justify-content-between gap-2 w-full">
                        <span className="text-2xs gu-muted">{formatPersianDate(update.createdAt, true)}</span>
                        <StatusChip status={update.goalStatus}/>
                    </div>
                    {KIND_LABELS[update.kind] && <span className="text-2xs font-bold" style={{color: "#a93100"}}>{KIND_LABELS[update.kind]}</span>}
                    {update.briff && <span className="text-xs" style={{lineHeight: 1.8, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden"}}>{richTextToPlain(update.briff)}</span>}
                    {update.author?.member && (
                        <span className="flex align-items-center gap-2 text-2xs gu-muted">
                            <Avatar memberData={update.author.member} style={{width: "1.4rem", height: "1.4rem"}} className="text-3xs"/>
                            {update.author.member.fullname}
                            {update.progress !== null && update.progress !== undefined && <span className="mr-auto">پیشرفت {Number(update.progress).toLocaleString("fa-IR")}٪</span>}
                        </span>
                    )}
                </button>
            ))}
            <div className="flex align-items-center gap-2 text-2xs gu-muted px-2">
                <Icon icon="mi mi:flag" className="text-sm"/>ایجاد هدف · {formatPersianDate(goalData?.createdAt)}
            </div>
            <GoalUpdateViewDialog
                visible={Boolean(selected)}
                update={selected?.documentId ? null : selected}
                updateId={selected?.documentId || null}
                goal={goalData}
                onHide={() => setSelected(null)}
            />
        </div>
    );
}

export default GoalUpdatesList;
